import React from "react";
import { Link } from "react-router-dom";
import appIcon from "../assets/gmail-collapse-all/icon-source.svg";
import demoVideo from "../assets/gmail-collapse-all/gmail-collapse-all-demo.mp4";
import ChromeWebStoreButton from "../components/ChromeWebStoreButton";
import styles from "../InverluxApps.module.css";

const GmailCollapseAll = () => (
  <>
    <section className={styles.productHero}>
      <div className={styles.contentWidth}>
        <div className={styles.productHeroGrid}>
          <div>
            <div className={styles.productIdentity}>
              <img
                className={styles.heroProductIcon}
                src={appIcon}
                alt="Collapse All for Gmail icon"
              />
              <div>
                <div className={styles.statusRow}>
                  <span className={styles.statusDot} aria-hidden="true" />
                  Free Chrome extension · Version 0.1
                </div>
                <h1>Collapse All for Gmail</h1>
              </div>
            </div>
            <p className={styles.heroLead}>
              Bring long Gmail conversations back under control with one
              focused, always-available button.
            </p>
            <p className={styles.heroSupporting}>
              Collapse All adds an accessible Collapse all control to Gmail
              conversation views. It stays available while you scroll and
              collapses only messages the extension can confidently identify
              as expanded.
            </p>
            <div className={styles.heroActions}>
              <ChromeWebStoreButton />
              <Link className={styles.textLink} to="/gmail-collapse-all/privacy">
                Read the Privacy Policy
              </Link>
            </div>
          </div>
          <div className={styles.heroAssurance}>
            <p className={styles.assuranceLabel}>Designed for one job</p>
            <ul className={styles.checkList}>
              <li>Always Available in the conversation (thread) view</li>
              <li>Uses Gmail’s built-in message collapse behavior</li>
              <li>Local processing with no extension backend</li>
              <li>No analytics or tracking</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className={styles.section} aria-labelledby="demonstration-title">
      <div className={styles.contentWidth}>
        <div className={styles.twoColumnSection}>
          <div className={styles.sectionHeading}>
            <p className={styles.eyebrow}>A shorter conversation view</p>
            <h2 id="demonstration-title">Collapse the open messages in one click.</h2>
            <p>
              The control remains within reach as you move through a Gmail
              conversation. When selected, it collapses the expanded messages
              the extension can identify with confidence.
            </p>
          </div>

          <figure className={styles.demoMedia}>
            <video
              className={styles.demoVideo}
              controls
              muted
              playsInline
              preload="metadata"
              aria-label="Demonstration of Collapse All for Gmail in a Gmail conversation"
            >
              <source src={demoVideo} type="video/mp4" />
              Your browser does not support embedded MP4 video.
            </video>
            <figcaption>
              Collapse expanded messages while the control remains available
              in conversation view.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section className={`${styles.section} ${styles.softSection}`}>
      <div className={styles.contentWidth}>
        <div className={styles.sectionHeading}>
          <p className={styles.eyebrow}>How it works</p>
          <h2>Purpose-built for Gmail conversation navigation.</h2>
        </div>
        <div className={styles.stepsGrid}>
          <article className={styles.stepCard}>
            <span className={styles.stepNumber}>01</span>
            <h3>Open a conversation</h3>
            <p>
              The button appears in conversation view, not in your inbox or
              other message-list views.
            </p>
          </article>
          <article className={styles.stepCard}>
            <span className={styles.stepNumber}>02</span>
            <h3>Keep scrolling</h3>
            <p>
              The Collapse all control stays accessible as you move through a
              long thread.
            </p>
          </article>
          <article className={styles.stepCard}>
            <span className={styles.stepNumber}>03</span>
            <h3>Collapse with confidence</h3>
            <p>
              One click collapses only the expanded messages the extension can
              confidently identify.
            </p>
          </article>
        </div>
      </div>
    </section>

    <section className={styles.section}>
      <div className={styles.contentWidth}>
        <div className={styles.privacyPanel}>
          <div>
            <p className={styles.eyebrow}>Privacy and permissions</p>
            <h2>Your Gmail content stays in your browser.</h2>
            <p>
              Collapse All for Gmail uses Manifest V3 and is limited to
              mail.google.com so it can integrate its control with Gmail’s
              conversation interface. Processing happens locally in the
              browser.
            </p>
          </div>
          <ul className={styles.privacyList}>
            <li>No email content collected, stored, or transmitted</li>
            <li>No accounts, analytics, or tracking</li>
            <li>No external backend or extension-initiated network requests</li>
          </ul>
          <Link className={styles.secondaryLink} to="/gmail-collapse-all/privacy">
            View the full Privacy Policy
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>

    <section className={`${styles.section} ${styles.faqSection}`}>
      <div className={styles.narrowWidth}>
        <div className={styles.sectionHeading}>
          <p className={styles.eyebrow}>FAQ</p>
          <h2>Questions, answered.</h2>
        </div>
        <div className={styles.faqList}>
          <details>
            <summary>Is Collapse All for Gmail free?</summary>
            <p>Yes. Version 0.1 is completely free.</p>
          </details>
          <details>
            <summary>Where does the button appear?</summary>
            <p>
              It appears only in Gmail conversation views. It does not appear
              in the inbox or other message-list views.
            </p>
          </details>
          <details>
            <summary>Does the extension collect my email content?</summary>
            <p>
              No. It does not collect, store, or transmit email subjects,
              bodies, senders, recipients, attachments, or other Gmail content.
            </p>
          </details>
          <details>
            <summary>Why does it need access to mail.google.com?</summary>
            <p>
              That access lets the extension identify Gmail conversation UI
              state and place the Collapse all control in the interface. Its
              access is limited to mail.google.com.
            </p>
          </details>
          <details>
            <summary>Does it connect to an Inverlux server?</summary>
            <p>
              No. The current version has no external backend and initiates no
              network requests. Its processing occurs locally in your browser.
            </p>
          </details>
        </div>
      </div>
    </section>

    <section className={styles.supportCta}>
      <div className={styles.narrowWidth}>
        <p className={styles.eyebrow}>Support</p>
        <h2>Need help with the extension?</h2>
        <p>Contact Inverlux Apps at support@inverlux.com.</p>
        <a className={styles.primaryLink} href="mailto:support@inverlux.com">
          Email support
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  </>
);

export default GmailCollapseAll;
